export type ActivityStatus =
  | "SUCCESS" | "FAILED" | "TIMEOUT" | "PARTIAL" | "ERROR" | "DETECTED";

export type ActivityCode =
  // LA - Login & Authentication
  | "la_login" | "la_logout" | "la_pwd" | "la_session" | "la_mfa"
  // DA - Data Activity
  | "da_view" | "da_create" | "da_update" | "da_delete" | "da_submit"
  | "da_approve" | "da_reject" | "da_return" | "da_cancel" | "da_close"
  | "da_reopen" | "da_print" | "da_preview"
  // IE - Import & Export
  | "ie_import" | "ie_export" | "ie_template"
  // FM - File Management
  | "fm_upload" | "fm_download" | "fm_replace" | "fm_delete" | "fm_preview"
  // AP - API Integration
  | "ap_request" | "ap_response"
  // ER - Error Log
  | "er_log"
  // SE - Security Event
  | "se_detect"
  // RP - Reporting
  | "rp_generate" | "rp_export" | "rp_print" | "rp_download";

export type ActivityObjectType = "MASTER" | "TRANSACTION";

export interface ActivityPayload {
  status: ActivityStatus;
  object_type?: ActivityObjectType;
  before_value?: any;
  after_value?: any;
  [key: string]: any;
}

type ActivityMeta = {
  categoryId: string;
  category: string;
  location: "Framework" | "Application";
  activity: string;
  allowedStatus: ActivityStatus[];
  objectType?: ActivityObjectType[];
  beforeValue?: true | "optional";
  afterValue?: true | "optional";
};

export const ACTIVITY_META: Record<ActivityCode, ActivityMeta> = {
  // Login & Authentication
  la_login:    { categoryId: "LA", category: "Login & Authentication", location: "Framework",   activity: "Login",                       allowedStatus: ["SUCCESS", "FAILED"] },
  la_logout:   { categoryId: "LA", category: "Login & Authentication", location: "Framework",   activity: "Logout",                      allowedStatus: ["SUCCESS", "FAILED"] },
  la_pwd:      { categoryId: "LA", category: "Login & Authentication", location: "Framework",   activity: "Password Management",         allowedStatus: ["SUCCESS", "FAILED"] },
  la_session:  { categoryId: "LA", category: "Login & Authentication", location: "Framework",   activity: "Session Management",          allowedStatus: ["SUCCESS", "TIMEOUT"] },
  la_mfa:      { categoryId: "LA", category: "Login & Authentication", location: "Framework",   activity: "Multi Factor Authentication", allowedStatus: ["SUCCESS", "FAILED"] },

  // Data Activity
  da_view:     { categoryId: "DA", category: "Data Activity", location: "Application", activity: "View",    allowedStatus: ["SUCCESS", "FAILED"] },
  da_create:   { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Create",  allowedStatus: ["SUCCESS", "FAILED"], objectType: ["MASTER", "TRANSACTION"],                afterValue: true },
  da_update:   { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Update",  allowedStatus: ["SUCCESS", "FAILED"], objectType: ["MASTER", "TRANSACTION"], beforeValue: true, afterValue: true },
  da_delete:   { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Delete",  allowedStatus: ["SUCCESS", "FAILED"], objectType: ["MASTER", "TRANSACTION"], beforeValue: true },
  da_submit:   { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Submit",  allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"] },
  da_approve:  { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Approve", allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"], beforeValue: "optional", afterValue: "optional" },
  da_reject:   { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Reject",  allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"], beforeValue: "optional", afterValue: "optional" },
  da_return:   { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Return",  allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"], beforeValue: "optional", afterValue: "optional" },
  da_cancel:   { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Cancel",  allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"], beforeValue: "optional", afterValue: "optional" },
  da_close:    { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Close",   allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"], beforeValue: "optional", afterValue: "optional" },
  da_reopen:   { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Reopen",  allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"], beforeValue: "optional", afterValue: "optional" },
  da_print:    { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Print",   allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"] },
  da_preview:  { categoryId: "DA", category: "Data Activity", location: "Application", activity: "Preview", allowedStatus: ["SUCCESS", "FAILED"], objectType: ["TRANSACTION"] },

  // Import & Export
  ie_import:   { categoryId: "IE", category: "Import & Export", location: "Application", activity: "Import",            allowedStatus: ["SUCCESS", "FAILED", "PARTIAL"] },
  ie_export:   { categoryId: "IE", category: "Import & Export", location: "Application", activity: "Export",            allowedStatus: ["SUCCESS", "FAILED"] },
  ie_template: { categoryId: "IE", category: "Import & Export", location: "Application", activity: "Download Template", allowedStatus: ["SUCCESS", "FAILED"] },

  // File Management
  fm_upload:   { categoryId: "FM", category: "File Management", location: "Application", activity: "Upload",   allowedStatus: ["SUCCESS", "FAILED"] },
  fm_download: { categoryId: "FM", category: "File Management", location: "Application", activity: "Download", allowedStatus: ["SUCCESS", "FAILED"] },
  fm_replace:  { categoryId: "FM", category: "File Management", location: "Application", activity: "Replace",  allowedStatus: ["SUCCESS", "FAILED"], beforeValue: true, afterValue: true },
  fm_delete:   { categoryId: "FM", category: "File Management", location: "Application", activity: "Delete",   allowedStatus: ["SUCCESS", "FAILED"], beforeValue: true },
  fm_preview:  { categoryId: "FM", category: "File Management", location: "Application", activity: "Preview",  allowedStatus: ["SUCCESS", "FAILED"] },

  // API Integration
  ap_request:  { categoryId: "AP", category: "API Integration", location: "Framework", activity: "API Request",  allowedStatus: ["SUCCESS", "FAILED", "TIMEOUT"] },
  ap_response: { categoryId: "AP", category: "API Integration", location: "Framework", activity: "API Response", allowedStatus: ["SUCCESS", "FAILED"] },

  // Error Log
  er_log:      { categoryId: "ER", category: "Error Log",      location: "Framework", activity: "Error Log",          allowedStatus: ["ERROR"] },

  // Security Event
  se_detect:   { categoryId: "SE", category: "Security Event", location: "Framework", activity: "Security Detection", allowedStatus: ["DETECTED"] },

  // Reporting
  rp_generate: { categoryId: "RP", category: "Reporting", location: "Application", activity: "Generate Report", allowedStatus: ["SUCCESS", "FAILED"] },
  rp_export:   { categoryId: "RP", category: "Reporting", location: "Application", activity: "Export Report",   allowedStatus: ["SUCCESS", "FAILED"] },
  rp_print:    { categoryId: "RP", category: "Reporting", location: "Application", activity: "Print Report",    allowedStatus: ["SUCCESS", "FAILED"] },
  rp_download: { categoryId: "RP", category: "Reporting", location: "Application", activity: "Download Report", allowedStatus: ["SUCCESS", "FAILED"] },
};

// ─────────────────────────────────────────────────────────────────────────────

type ActivityItem = {
  event: string;
  payload: any;
  timestamp: number;
};

interface ActivityConfig {
  endpoint: string;
  headers?: Record<string, string>;
  batchSize?: number;
  interval?: number;
}

export interface StartOptions {
  headers?: Record<string, string>;
  requireAuth?: boolean;
}

interface ActivityReporter {
  start(opts?: StartOptions): void;
  track(event: ActivityCode | string, payload?: ActivityPayload | Record<string, any>): void;
  flush(): Promise<void>;
  stop(): void;
  destroy(): void;
}

export function activity(config: ActivityConfig): ActivityReporter {
  const { endpoint, batchSize = 20, interval = 20_000 } = config;

  let activeHeaders: Record<string, string> = { ...(config.headers ?? {}) };
  let requireAuth = false;

  let queue: ActivityItem[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;
  let isFlushing = false;

  async function flush(): Promise<void> {
    if (isFlushing || queue.length === 0) return;
    if (requireAuth && !activeHeaders["Authorization"]) return;

    isFlushing = true;
    const batch = queue.splice(0, queue.length);

    try {
      await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...activeHeaders },
        body: JSON.stringify({ activities: batch }),
      });
    } catch {
      queue.unshift(...batch);
    } finally {
      isFlushing = false;
    }
  }

  function track(event: ActivityCode | string, payload: ActivityPayload | Record<string, any> = {}): void {
    queue.push({ event, payload, timestamp: Date.now() });
    if (queue.length >= batchSize) flush();
  }

  function start(opts: StartOptions = {}): void {
    if (timer) return;
    if (opts.headers) Object.assign(activeHeaders, opts.headers);
    if (opts.requireAuth !== undefined) requireAuth = opts.requireAuth;
    timer = setInterval(() => {
      if (queue.length > 0) flush();
    }, interval);
  }

  function stop(): void {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function destroy(): void {
    stop();
    queue = [];
  }

  return { start, track, flush, stop, destroy };
}
